import {
  CustomButton,
  CustomTable,
  SkeletonLoader,
  SvgEmptyTable,
  TableColumnType,
} from '@ui-kit'
import { useEffect, useState } from 'react'
import {
  addToaster,
  hideLoader,
  showLoader,
  addEPRPatient,
  fetchEPRQuery,
  getGenericEntityListingAPI,
  editEntitiesData,
  addEntitiesData,
} from '@app-store'
import { NAMESPACES } from '../../utils/constants'
import { FieldValues } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { IEPRData } from '../../types/workflow.types'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { uniqueId } from 'lodash'

const EPRQueryModal: React.FC<{
  patientData: FieldValues
  metaData?: { layout_id: string; entity_id: string; entity_name: string }
  closeModal: () => void
  pharmacyEntityName?: string
  distributor?: string
}> = ({ patientData, closeModal, pharmacyEntityName, distributor }) => {
  const dispatch = useAppDispatch()
  const params = useParams()
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const columns: TableColumnType<IEPRData>[] = [
    {
      header: '',
      accessor: 'select',
      type: 'radio',
    },
    {
      header: NAMESPACES.PATIENT_FIRST_NAME,
      accessor: 'patient_first_name',
    },
    {
      header: NAMESPACES.PATIENT_LAST_NAME,
      accessor: 'patient_last_name',
    },
    {
      header: NAMESPACES.PATIENT_DOB,
      accessor: 'patient_dob',
    },
    {
      header: NAMESPACES.PHARMACY_ID,
      accessor: 'pharmacy_id',
    },
    {
      header: NAMESPACES.ZIPCODE,
      accessor: 'zipcode',
    },
    {
      header: NAMESPACES.ADDRESS_LINE_1,
      accessor: 'address_line_1',
    },
    {
      header: NAMESPACES.ADDRESS_LINE_2,
      accessor: 'address_line_2',
    },
  ]

  const [data, setData] = useState<IEPRData[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true)

  const onColumnClick = (
    _isHeader: boolean,
    KeyName: keyof IEPRData,
    _row?: IEPRData | null,
    rowIndex: number | null = null
  ) => {
    switch (KeyName) {
      case 'select':
        if (rowIndex !== null) {
          selectRow(rowIndex)
          setSaveDisabled(false)
        }
    }
  }

  const selectRow = (index: number) => {
    setData((prevData) =>
      prevData.map((item, i) => ({
        ...item,
        select: i === index,
      }))
    )
    setSaveDisabled(false)
  }

  const fetchEPRPatients = async () => {
    try {
      setLoading(true)
      const caseIdObject2 = await fetchEPRQuery(
        params?.caseId ?? '',
        patientData?.patient?.id ?? ''
      )
      const listData =
        caseIdObject2?.data?.body?.data?.map((item: FieldValues) => ({
          pharmacy_id: item?.pharmacy_id,
          patient_first_name: item?.patient_first_name ?? '',
          patient_last_name: item?.patient_last_name ?? '',
          patient_dob: item?.patient_dob ?? '',
        })) ?? []
      if (listData?.length) setData(listData)
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddPatient = async () => {
    try {
      dispatch(showLoader())
      const response = await addEPRPatient(
        parseInt(params?.caseId ?? '0'),
        patientData?.patient?.id
      )
      if (response) {
        fetchEPRPatients()
      }
    } catch (err) {
      const status = (err as AxiosError).response?.status
      const data = (err as AxiosError)?.response?.data
      const message =
        (data as unknown as { message: string })?.message ??
        (data as unknown as { body: Record<string, FieldValues> })?.body
          ?.RxcomFailureInfo?.RxcomFailureDescription?._text ??
        ''
      if (status === 400) {
        dispatch(
          addToaster({
            id: uniqueId(),
            message,
            type: 'error',
          })
        )
      }
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handlePatientSave = async (selectedPatient?: IEPRData) => {
    try {
      dispatch(showLoader())
      const payload = {
        select: ['id', 'status'],
        entity_name: pharmacyEntityName,
        filters: [
          {
            criteria: [
              {
                operation: 'EQUALS',
                field_name: 'patient_id',
                values: [patientData?.patient?.id],
              },
              {
                operation: 'EQUALS',
                field_name: 'pharmacy_id',
                values: [selectedPatient?.pharmacy_id],
              },
              {
                operation: 'EQUALS',
                field_name: 'status',
                values: ['INACTIVE'],
              },
            ],
          },
        ],
      }
      const caseIdObject2 = await getGenericEntityListingAPI(payload)
      const data = {
        pharmacy_id: selectedPatient?.pharmacy_id,
        distributor: distributor ?? '',
        patient_id: patientData?.patient?.id,
        status: 'ACTIVE',
        _state: 'ADDED',
      }
      const recordId = caseIdObject2?.[0]?.id
      let response
      if (recordId) {
        response = await editEntitiesData(
          pharmacyEntityName ?? '',
          data,
          recordId,
          [NAMESPACES.STATUS_KEY]
        )
      } else if (Array.isArray(caseIdObject2) && caseIdObject2?.length === 0) {
        response = await addEntitiesData(pharmacyEntityName ?? '', data, [
          NAMESPACES.STATUS_KEY,
        ])
      }
      if (response?.data) {
        dispatch(
          addToaster({
            id: new Date().getTime()?.toString(),
            message: NAMESPACES?.DETAILS_UPDATED_SUCCESSFULLY,
            type: 'success',
          })
        )
      }
      const dispatchevent = new CustomEvent('EPR_PATIENT_UPDATED', {
        detail: { patientId: patientData?.patient?.id },
      })
      window?.dispatchEvent(dispatchevent)
      closeModal()
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    if (workflowDefinition && patientData) fetchEPRPatients()
  }, [workflowDefinition, patientData])

  if (loading) {
    return (
      <div className="flex flex-col w-full max-h-full min-h-[18rem] overflow-hidden">
        <div className="flex justify-between bg-accent-25 items-center p-2 shrink-0">
          <div className="flex flex-col gap-2">
            <div className="h-10 animate-pulse bg-accent-100 w-96 max-w-full"></div>
            <div className="h-6 animate-pulse bg-accent-100 w-48 max-w-full"></div>
          </div>
          <div className="flex gap-2 shrink-0">
            <div className="h-10 w-24 bg-accent-100"></div>
            <div className="h-10 w-24 bg-accent-100"></div>
          </div>
        </div>
        <div className="flex-1 min-h-0 w-full overflow-hidden">
          <SkeletonLoader rows={3} />
        </div>
      </div>
    )
  }
  return (
    <div className="flex flex-col w-full h-[75vh] max-h-full min-h-[18rem] overflow-hidden">
      <div className="flex items-center justify-between gap-2 pr-6 pl-3 py-3 shrink-0">
        <div className="flex flex-col min-w-0 shrink">
          <span className="text-lg font-medium whitespace-nowrap">
            {NAMESPACES.PHARMACY_DETAILS_FOUND}
          </span>
          <span className="font-light text-sm whitespace-nowrap">
            {data?.length > 0
              ? `${data.length} ${NAMESPACES?.RESULTS_FOUND}`
              : `${NAMESPACES?.NO_RESULTS_FOUND}`}
          </span>
        </div>
        <div className="flex gap-2 shrink-0 whitespace-nowrap">
          <CustomButton
            isFilled
            variant="sm"
            disabled={!saveDisabled}
            onClick={() => {
              handleAddPatient()
            }}
          >
            {NAMESPACES?.ADD_PATIENT}
          </CustomButton>
          <CustomButton
            isFilled
            variant="sm"
            disabled={saveDisabled || data?.length === 0}
            onClick={() => handlePatientSave(data.find((item) => item.select))}
          >
            {NAMESPACES?.SAVE}
          </CustomButton>
        </div>
      </div>
      {data?.length === 0 ? (
        <div className="flex-1 min-h-[14rem] bg-secondary-25 w-full flex items-center justify-center flex-col overflow-hidden">
          <SvgEmptyTable />
          <span className="text-sm font-light leading-lg">
            {NAMESPACES?.NO_PHARMACY_DETAILS_FOUND}
          </span>
        </div>
      ) : (
        <div
          className="flex-1 basis-0 min-h-[14rem] overflow-hidden border border-accent-200 rounded p-1
            [&>div]:!h-full [&>div]:!min-h-0 [&>div]:!overflow-auto
            [&>div>div.flex-1]:!flex-none [&>div>div.flex-1]:!h-auto [&>div>div.flex-1]:!max-h-none [&>div>div.flex-1]:!min-h-0 [&>div>div.flex-1]:!overflow-visible"
        >
          <CustomTable
            columns={columns}
            data={data}
            showTableColumnOnEmpty
            onRowClick={(_row, rowIndex) => {
              selectRow(rowIndex)
            }}
            onColumnClick={(isHeader, KeyName, row, rowIndex) => {
              onColumnClick(isHeader, KeyName, row, rowIndex)
            }}
          />
        </div>
      )}
    </div>
  )
}

export default EPRQueryModal
